import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RemoveDialog } from "@/components/hub/RemoveDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MoneyField, PercentField, CountField, PhoneField, DEAL_PRESETS } from "@/components/ui/number-fields";
import { Plus, Loader2, Pencil, Trash2, Link2, Copy, Check, RefreshCw, FileSignature, FileDown, KeyRound, QrCode, Code2 } from "lucide-react";
import { toast } from "sonner";

import { AgreementAdminDialog } from "./AgreementAdminDialog";
import { TermsChangePrompt } from "@/components/hub/TermsChangePrompt";
import { AssignManagerDialog } from "@/components/hub/AssignManagerDialog";
import { MutedPill, MutedNotice } from "@/components/hub/MutedPill";
import { ApplyLinkDialog } from "@/components/hub/ApplyLinkDialog";
import { SeatsDialog } from "@/components/hub/SeatsDialog";
import { ApiAccessDialog } from "@/components/hub/ApiAccessDialog";
import { ApprovePartnerDialog } from "@/components/hub/ApprovePartnerDialog";
import { useSearchParams } from "react-router-dom";
import { ADMIN_COMMISSION_TYPES, COMMISSION_IS_PCT, commissionSummary, COUNTRIES } from "./constants";
import {
  Btn, CellMain, CellSub, Empty, Money, PageHead, Panel, Sec, St, StatStrip, Tag, Tb, Td, Th,
} from "@/components/portal/crm/kit";

/** A square icon control that belongs to the v3 system rather than Tailwind. */
const IconBtn = ({ children, onClick, title, testId, danger }) => (
  <button onClick={onClick} title={title} data-testid={testId}
          style={{ display: "grid", placeItems: "center", width: 32, height: 32, border: 0, borderRadius: 9,
            background: "transparent", cursor: "pointer", color: danger ? "var(--red)" : "var(--mut)" }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--hover)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
    {children}
  </button>
);

export default function HubReferrers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [termsMoved, setTermsMoved] = useState(null); // { party, change }
  const [managers, setManagers] = useState([]);
  const [assignFor, setAssignFor] = useState(null);

  const loadManagers = () => api.get("/hub/managers")
    .then((r) => setManagers(r.data.items || [])).catch(() => setManagers([]));
  useEffect(() => { loadManagers(); }, []);
  const managerName = (id) => managers.find((m) => m.id === id)?.name;

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/hub/referrers"); setRows(r.data.items || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const [agreementFor, setAgreementFor] = useState(null);
  const [statementFor, setStatementFor] = useState(null);
  const [linkFor, setLinkFor] = useState(null);
  const [seatsFor, setSeatsFor] = useState(null);
  const [apiFor, setApiFor] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [params, setParams] = useSearchParams();
  const onlyPending = params.get("pending") === "1";

  const regenerate = async (id) => {
    try { const r = await api.post(`/hub/referrers/${id}/invite`); setRows((rs) => rs.map((x) => x.id === id ? { ...x, invite: r.data.invite, activated: false } : x)); toast.success("Invite regenerated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendAccessLink = async (id) => {
    try {
      const r = await api.post(`/hub/partners/referrer/${id}/access-link`);
      toast.success(r.data.action === "reset" ? "Password reset link emailed + texted" : "Set-up link emailed + texted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the link"); }
  };

  const activeCount = rows.filter((r) => r.status === "active").length;
  const signed = rows.filter((r) => r.agreement?.status === "signed").length;
  const noBank = rows.filter((r) => !r.payout_ready).length;
  const seatTotal = rows.reduce((a, r) => a + (r.seat_count || 0), 0);
  const withSeats = rows.filter((r) => r.seat_count > 0).length;
  const pendingRows = rows.filter((r) => r.status === "pending");
  const shown = onlyPending ? pendingRows : rows;

  return (
    <div data-testid="hub-referrers">
      <PageHead
        eyebrow="Network"
        title="Introducers"
        lede="Invite-only partners who bring us cases. Their commission is worked out automatically on every completed deal and paid the month after."
        testId="hub-referrers-head"
        actions={
          <>
            <Btn onClick={() => setEditing({ partner_kind: "campaign" })} testId="hub-campaign-new">New campaign partner</Btn>
            <Btn kind="primary" onClick={() => setEditing("new")} testId="hub-referrer-new">New introducer</Btn>
          </>
        }
      />

      <StatStrip
        testId="hub-referrers-stats"
        items={[
          { k: "Introducers", v: String(rows.length), h: `${activeCount} active${pendingRows.length ? ` · ${pendingRows.length} awaiting approval` : ""}`, testId: "hub-referrers-stat-total" },
          { k: "Agreements signed", v: String(signed), h: rows.length ? `${rows.length - signed} outstanding` : "—", testId: "hub-referrers-stat-signed" },
          { k: "Missing bank details", v: String(noBank), h: noBank ? "Statements print without payment details" : "Everyone is payable", testId: "hub-referrers-stat-bank" },
          { k: "Seats", v: String(seatTotal), h: seatTotal ? `Across ${withSeats} partner${withSeats === 1 ? "" : "s"} — call centres and teams` : "No partner has added agents yet", testId: "hub-referrers-stat-seats" },
        ]}
      />

      {pendingRows.length > 0 && (
        <div className="warn" data-testid="hub-pending-banner" style={{ marginTop: 22, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <span>
            <b>{pendingRows.length} partner{pendingRows.length === 1 ? "" : "s"} signed up and {pendingRows.length === 1 ? "is" : "are"} waiting on you.</b>{" "}
            They can sign in already; cases and links stay locked until you approve.
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => setParams(onlyPending ? {} : { pending: "1" })} data-testid="hub-pending-toggle">
            {onlyPending ? "Show everyone" : "Show only those waiting"}
          </button>
        </div>
      )}

      <Sec>
        <Panel pad={false} testId="hub-referrers-panel" style={{ overflow: "hidden" }}
               footer={<span>Changing a rate supersedes any signed agreement and tells the partner on every channel. Their existing cases keep the rate they were introduced at.</span>}>
          {loading ? (
            <Empty testId="hub-referrers-loading">Reading the network…</Empty>
          ) : shown.length === 0 ? (
            <Empty testId="hub-referrers-empty">{onlyPending ? "Nobody is waiting for approval." : "No introducers yet. Add one to start receiving cases."}</Empty>
          ) : (
            <Tb testId="hub-referrers-table"
                head={<>
                  <Th style={{ width: "24%" }}>Partner</Th>
                  <Th style={{ width: "15%" }}>Company</Th>
                  <Th className="r" style={{ width: "12%" }}>Their share</Th>
                  <Th style={{ width: "11%" }}>Seats</Th>
                  <Th style={{ width: "20%" }}>Status</Th>
                  <Th style={{ width: "9%" }}>Access</Th>
                  <Th className="r" style={{ width: "9%" }}>&nbsp;</Th>
                </>}>
              {shown.map((R) => (
                <tr key={R.id} data-testid={`hub-referrer-row-${R.id}`} style={R.status === "pending" ? { background: "rgba(255,180,0,.05)" } : undefined}>
                  <Td className="lead" style={{ padding: "20px" }}>
                    <CellMain style={{ fontSize: 14.5 }}>
                      {R.name}
                      {R.partner_kind === "campaign" && <span style={{ marginLeft: 8, verticalAlign: "middle" }} data-testid={`hub-referrer-kind-${R.id}`}><Tag>Campaign</Tag></span>}
                      {R.floor && <span style={{ marginLeft: 8, verticalAlign: "middle" }} data-testid={`hub-referrer-floor-${R.id}`}><Tag>{R.master ? "Master introducer" : "Call centre"}</Tag></span>}
                      {R.api_enabled && <span style={{ marginLeft: 8, verticalAlign: "middle" }} data-testid={`hub-referrer-api-tag-${R.id}`}><Tag>API</Tag></span>}
                    </CellMain>
                    <CellSub>{R.email}{R.phone ? ` · ${R.phone}` : ""}</CellSub>
                    {R.referred_by_name && (
                      <CellSub style={{ marginTop: 4 }} data-testid={`hub-referrer-network-${R.id}`}>
                        Recruited by <b style={{ color: "var(--ink)" }}>{R.referred_by_name}</b>{R.override_pct != null ? ` · ${R.override_pct}% override` : ""}
                      </CellSub>
                    )}
                    <CellSub style={{ marginTop: 6 }}>
                      <button onClick={() => setAssignFor(R)} data-testid={`hub-referrer-manager-${R.id}`}
                              title="Their Connexis route — a recruiter, where they have one, still comes first"
                              style={{ border: 0, background: "none", padding: 0, font: "inherit", cursor: "pointer",
                                color: managerName(R.account_manager_id) ? "var(--mut)" : "var(--accent-text)",
                                textDecoration: "underline", textDecorationStyle: "dotted" }}>
                        {managerName(R.account_manager_id)
                          ? `Managed by ${managerName(R.account_manager_id)}`
                          : "No manager — assign one"}
                      </button>
                    </CellSub>
                  </Td>
                  <Td><CellMain style={{ fontSize: 13.5 }}>{R.company || "—"}</CellMain></Td>
                  <Td className="r">
                    <Money raw={commissionSummary(R).rate} />
                    <CellSub>{commissionSummary(R).note}</CellSub>
                  </Td>
                  <Td>
                    {R.partner_kind === "campaign" ? (
                      <CellSub data-testid={`hub-referrer-seats-${R.id}`}>—</CellSub>
                    ) : R.seat_count > 0 ? (
                      <button onClick={() => setSeatsFor(R)} data-testid={`hub-referrer-seats-${R.id}`}
                              title={(R.seat_names || []).join(", ")}
                              style={{ border: 0, background: "none", padding: 0, font: "inherit", cursor: "pointer", textAlign: "left" }}>
                        <CellMain style={{ fontSize: 13.5, color: "var(--accent-text)" }}>{R.seat_count} seat{R.seat_count === 1 ? "" : "s"}</CellMain>
                        <CellSub>{(R.seat_names || []).slice(0, 2).join(", ")}{R.seat_count > 2 ? ` +${R.seat_count - 2}` : ""}</CellSub>
                      </button>
                    ) : (
                      <CellSub data-testid={`hub-referrer-seats-${R.id}`}>Account holder only</CellSub>
                    )}
                  </Td>
                  <Td>
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                      {R.status === "pending" ? (
                        <>
                          <span data-testid={`hub-referrer-status-${R.id}`}><Tag amber>Awaiting approval</Tag></span>
                          <button className="btn btn-primary btn-sm" onClick={() => setApproveFor(R)} data-testid={`hub-referrer-approve-${R.id}`}>Review &amp; approve</button>
                        </>
                      ) : R.status === "declined" ? (
                        <St tone="slate" testId={`hub-referrer-status-${R.id}`}>declined</St>
                      ) : (
                        <St tone={R.status === "active" ? "mint" : "slate"} testId={`hub-referrer-status-${R.id}`}>{R.status}</St>
                      )}
                      {R.signup?.kind && <span data-testid={`hub-referrer-signup-kind-${R.id}`}><Tag>{R.signup.kind === "media" ? "Signed up · media" : "Signed up · call centre"}</Tag></span>}
                      <MutedPill summary={R.notify_summary} id={R.id} />
                      {R.agreement?.status === "signed" && <Tag>Signed</Tag>}
                      {!R.payout_ready && R.status !== "pending" && R.status !== "declined" && (
                        <span data-testid={`hub-referrer-payout-missing-${R.id}`}
                              title="No bank details on file — statements will print without payment details">
                          <Tag amber>No bank details</Tag>
                        </span>
                      )}
                    </div>
                    {R.terms_changed && (
                      <button onClick={() => setAgreementFor(R)} className="btn btn-ghost btn-sm" style={{ marginTop: 9 }}
                              title="Signed agreement is on the old rate — re-issue at the new terms"
                              data-testid={`hub-referrer-terms-changed-${R.id}`}>
                        Terms changed — re-issue
                      </button>
                    )}
                  </Td>
                  <Td>
                    <InviteCell invite={R.invite} activated={R.activated} onRegenerate={() => regenerate(R.id)}
                                onSend={() => sendAccessLink(R.id)} testId={`hub-referrer-invite-${R.id}`} />
                  </Td>
                  <Td className="r">
                    <div style={{ display: "inline-flex", gap: 2 }}>
                      {R.partner_kind !== "campaign" && (
                        <IconBtn onClick={() => setApiFor(R)} testId={`hub-referrer-api-${R.id}`}
                                 title={R.api_enabled ? "API access is on — manage it" : "API access — switch it on for this introducer"}>
                          <Code2 className={`size-4 ${R.api_enabled ? "text-[#0055FF]" : ""}`} />
                        </IconBtn>
                      )}
                      <IconBtn onClick={() => setLinkFor(R)} testId={`hub-referrer-applylink-${R.id}`}
                               title="Their application link — copy it or grab the QR"><QrCode className="size-4" /></IconBtn>
                      <IconBtn onClick={() => setAgreementFor(R)} testId={`hub-referrer-agreement-${R.id}`}
                               title={R.agreement?.status === "signed" ? `Executed by ${R.agreement.signed_name}` : "Agreement — send, download or countersign"}>
                        <FileSignature className={`size-4 ${R.agreement?.status === "signed" ? "text-teal-600" : ""}`} />
                      </IconBtn>
                      <IconBtn onClick={() => setStatementFor(R)} testId={`hub-referrer-statement-${R.id}`}
                               title="Download a commission statement PDF"><FileDown className="size-4" /></IconBtn>
                      <IconBtn onClick={() => setEditing(R)} testId={`hub-referrer-edit-${R.id}`} title="Edit"><Pencil className="size-4" /></IconBtn>
                      <IconBtn onClick={() => setToDelete(R)} testId={`hub-referrer-delete-${R.id}`} title="Delete" danger><Trash2 className="size-4" /></IconBtn>
                    </div>
                  </Td>
                </tr>
              ))}
            </Tb>
          )}
        </Panel>
      </Sec>

      <ReferrerDialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}
                      initial={editing === "new" ? null : editing} recruiters={rows}
                      onSaved={(res) => { setEditing(null); load(); if (res?.change?.changed) setTermsMoved(res); }} />

      <TermsChangePrompt kind="referrer" party={termsMoved?.party} change={termsMoved?.change}
                         onOpenChange={(o) => !o && setTermsMoved(null)} onSent={load} />

      <AgreementAdminDialog kind="referrer" party={agreementFor} onOpenChange={(o) => !o && setAgreementFor(null)} onChanged={load} />

      <StatementDialog party={statementFor} onOpenChange={(o) => !o && setStatementFor(null)} />

      <ApplyLinkDialog party={linkFor} onOpenChange={(o) => !o && setLinkFor(null)} />

      <SeatsDialog party={seatsFor} onOpenChange={(o) => !o && setSeatsFor(null)} />

      <ApiAccessDialog party={apiFor} onOpenChange={(o) => !o && setApiFor(null)} onChanged={load} />

      <ApprovePartnerDialog party={approveFor} onOpenChange={(o) => !o && setApproveFor(null)}
                            onDone={(updated) => setRows((rs) => rs.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))} />

      <RemoveDialog kind="referrer" id={toDelete?.id} name={toDelete?.name}
                    open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}
                    onDeleted={() => { setRows((rs) => rs.filter((r) => r.id !== toDelete.id)); setToDelete(null); }} />

      <AssignManagerDialog kind="referrer" party={assignFor} open={!!assignFor}
                           onOpenChange={(o) => !o && setAssignFor(null)}
                           onDone={() => { loadManagers(); load(); }} />
    </div>
  );
}

const EMPTY = {
  name: "", email: "", company: "", phone: "",
  commission_type: "revenue_share", commission_pct: "", commission_flat_gbp: "",
  payout_frequency: "monthly", status: "invited", partner_kind: "introducer", floor: false, master: false,
  bank_account_name: "", bank_sort_code: "", bank_account_no: "", vat_number: "",
  referred_by: "", override_pct: "", countries_covered: [],
};

function ReferrerDialog({ open, onOpenChange, initial, onSaved, recruiters = [] }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;
  const campaign = (form.partner_kind || "introducer") === "campaign";
  const kindLabel = campaign ? "Campaign partner" : form.master ? "Master introducer" : form.floor ? "Call centre" : "Introducer";
  const recruiterOptions = recruiters.filter((r) => r.id !== initial?.id && r.partner_kind !== "campaign" && !r.house);
  const recruiter = recruiterOptions.find((r) => r.id === form.referred_by);

  useEffect(() => {
    setForm(initial ? {
      ...EMPTY, ...initial,
      company: initial.company || "",
      phone: initial.phone || "",
      vat_number: initial.vat_number || "",
      bank_account_name: initial.bank_account_name || "",
      bank_sort_code: initial.bank_sort_code || "",
      bank_account_no: initial.bank_account_no || "",
      countries_covered: initial.countries_covered || [],
      commission_pct: initial.commission_pct || "",
      commission_flat_gbp: initial.commission_flat_gbp || "",
      referred_by: initial.referred_by || "",
      override_pct: initial.override_pct ?? "",
    } : EMPTY);
  }, [initial, open]);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCountry = (c) => setForm((f) => ({ ...f, countries_covered: (f.countries_covered || []).includes(c) ? f.countries_covered.filter((x) => x !== c) : [...(f.countries_covered || []), c] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name + email required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        commission_pct:      parseFloat(form.commission_pct)      || 0,
        commission_flat_gbp: parseFloat(form.commission_flat_gbp) || 0,
        referred_by:         form.referred_by || null,
        override_pct:        form.referred_by && form.override_pct !== "" ? parseFloat(form.override_pct) : null,
      };
      const r = isEdit ? await api.put(`/hub/referrers/${initial.id}`, payload)
                       : await api.post("/hub/referrers", payload);
      const what = kindLabel;
      toast.success(isEdit ? `${what} updated`
        : r.data?.invite_emailed ? `${what} created — portal login link emailed + texted to them`
        : `${what} created — invite link ready to copy (email couldn't be delivered)`);
      onSaved({ party: r.data, change: r.data?.terms_change });
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const isPct = COMMISSION_IS_PCT(form.commission_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="hub-referrer-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${kindLabel.toLowerCase()}` : `New ${kindLabel.toLowerCase()}`}</DialogTitle>
          <DialogDescription>
            {campaign
              ? "One click: they get the campaign portal login by email and text, with their tracking links ready. They never see who applied."
              : form.master
                ? "Invite only. The call-centre floor with agent seats, plus tracked ad links and channel stats — and they see their clients in full."
              : form.floor
                ? "A call centre gets the floor portal: their own agent seats, each agent seeing only their own cases, and an internal share they set per agent."
                : "Invite via a private link — never exposed on the public site."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <SectionH>Contact</SectionH>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Name *"><Input value={form.name} onChange={setField("name")} data-testid="referrer-name" /></F>
            <F label="Email *"><Input type="email" value={form.email} onChange={setField("email")} data-testid="referrer-email" /></F>
            <F label="Company"><Input value={form.company} onChange={setField("company")} /></F>
            <F label="Phone"><Input value={form.phone} onChange={setField("phone")} /></F>
          </div>

          <SectionH>Partner type</SectionH>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              ["introducer", false, false, "Introducer", "Brings and works cases themselves. Sees their clients, the desks' stages, documents and the thread."],
              ["introducer", true, false, "Call centre", "Runs a floor of agents. Portal opens on the floor: seats, who's bringing cases, and an internal share per agent."],
              ["campaign", false, false, "Campaign partner", "Media, affiliates, ad buyers. Sees links, stats and earnings — never who applied. Cases arrive through their links only."],
              ["introducer", true, true, "Master introducer", "Invite only. Call-centre floor and seats plus tracked ad links and channel stats, with full client details."],
            ].map(([k, isFloor, isMaster, label, hint]) => {
              const on = (form.partner_kind || "introducer") === k && !!form.floor === isFloor && !!form.master === isMaster;
              return (
                <button key={`${k}-${isFloor}-${isMaster}`} type="button" onClick={() => setForm((f) => ({ ...f, partner_kind: k, floor: isFloor, master: isMaster }))}
                        className={`text-left rounded-lg border p-2.5 transition-colors ${on ? "bg-[#0055FF]/[0.06] border-[#0055FF]/50" : "bg-white border-slate-300 hover:border-slate-400"}`}
                        data-testid={`referrer-kind-${isMaster ? "master" : isFloor ? "floor" : k}`}>
                  <div className="text-[12.5px] font-semibold text-slate-900">{label}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5 leading-snug">{hint}</div>
                </button>
              );
            })}
          </div>

          {!campaign && (
            <div>
              <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Countries covered</Label>
              <div className="mt-2 flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1" data-testid="referrer-countries">
                {COUNTRIES.map((c) => {
                  const active = (form.countries_covered || []).includes(c);
                  return (
                    <button type="button" key={c} onClick={() => toggleCountry(c)}
                      className={`px-3 py-1.5 rounded-full border text-[12px] font-medium ${active ? "border-[#0055FF] bg-[#0055FF] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                      data-testid={`referrer-country-${c.replace(/[^a-zA-Z]+/g, '-').toLowerCase()}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <SectionH>Recruited by</SectionH>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Introducer who brought them in">
              <Select value={form.referred_by || "none"} onValueChange={(v) => setForm((f) => ({ ...f, referred_by: v === "none" ? "" : v, override_pct: v === "none" ? "" : f.override_pct }))}>
                <SelectTrigger data-testid="referrer-recruited-by"><SelectValue placeholder="Nobody — Connexis signed them" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nobody — Connexis signed them</SelectItem>
                  {recruiterOptions.map((r) => (
                    <SelectItem key={r.id} value={r.id} data-testid={`referrer-recruiter-${r.id}`}>{r.company || r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            {form.referred_by && (
              <F label={`${recruiter?.company || recruiter?.name || "Their"} override`}>
                <PercentField value={form.override_pct} onChange={(v) => setForm((f) => ({ ...f, override_pct: v }))}
                              max={50} step={0.5} presets={[5, 10, 15, 20]}
                              hint={`of our earnings on every deal this partner produces — paid by Connexis. Blank = their usual ${recruiter?.network_override_pct_introducer ?? 10}%`}
                              testId="referrer-override-pct" />
              </F>
            )}
          </div>

          <SectionH>Commission</SectionH>
          <div>
            <Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Model</Label>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              {ADMIN_COMMISSION_TYPES.map((c) => (
                <button key={c.key} type="button" onClick={() => setForm((f) => ({ ...f, commission_type: c.key }))}
                        className={`text-left rounded-lg border p-2.5 transition-colors ${form.commission_type === c.key ? "bg-[#0055FF]/[0.06] border-[#0055FF]/50" : "bg-white border-slate-300 hover:border-slate-400"}`}
                        data-testid={`referrer-comm-${c.key}`}>
                  <div className="text-[12.5px] font-semibold text-slate-900">{c.label}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5 leading-snug">{c.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {isPct ? (
              <F label={form.commission_type === "commission_share" ? "% of our commission" : "% of funded amount"}>
                <PercentField value={form.commission_pct} onChange={(v) => setForm((f) => ({ ...f, commission_pct: v }))}
                              max={100} presets={form.commission_type === "commission_share" ? [25, 40, 50, 60, 75] : [1.5, 5, 10, 20, 50]}
                              hint={form.commission_type === "commission_share" ? "share of our fee on each deal" : "of the funded finance amount"}
                              testId="referrer-commission-pct" />
              </F>
            ) : (
              <F label="Fixed amount (£)"><MoneyField value={form.commission_flat_gbp} onChange={(v) => setForm((f) => ({ ...f, commission_flat_gbp: v }))} ladder="fee" compact testId="referrer-commission-flat" hint="per funded deal / lead" /></F>
            )}
            <F label="Payout schedule">
              <Select value={form.payout_frequency || "monthly"} onValueChange={(v) => setForm((f) => ({ ...f, payout_frequency: v }))}>
                <SelectTrigger data-testid="referrer-payout-freq"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly · 1st</SelectItem>
                  <SelectItem value="weekly">Weekly · Friday</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="invited">Invited</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="pending">Awaiting approval</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>

          <SectionH>Payout details</SectionH>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Bank account name"><Input value={form.bank_account_name} onChange={setField("bank_account_name")} /></F>
            <F label="VAT number"><Input value={form.vat_number} onChange={setField("vat_number")} /></F>
            <F label="Sort code"><Input value={form.bank_sort_code} onChange={setField("bank_sort_code")} placeholder="00-00-00" /></F>
            <F label="Account number"><Input value={form.bank_account_no} onChange={setField("bank_account_no")} /></F>
          </div>

          {isEdit && <MutedNotice summary={initial.notify_summary} name={initial.name} />}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="hub-referrer-submit">{busy ? <Loader2 className="size-4 animate-spin" /> : isEdit ? "Save" : "Create"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SectionH({ children }) {
  return <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-slate-500 border-t border-slate-100 pt-3 first:border-none first:pt-0">{children}</div>;
}

function InviteCell({ invite, activated, onRegenerate, onSend, testId }) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const send = async () => { setSending(true); try { await onSend?.(); } finally { setSending(false); } };
  const SendBtn = ({ label }) => (
    <button onClick={send} disabled={sending} className="p-1 rounded hover:bg-slate-100 text-slate-500" title={label} data-testid={`${testId}-send`}>
      {sending ? <Loader2 className="size-3.5 animate-spin" /> : <KeyRound className="size-3.5" />}
    </button>
  );
  if (activated) return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <span className="inline-flex items-center gap-1 text-[11px] text-teal-600 font-semibold"><Check className="size-3" /> Active</span>
      <SendBtn label="Email + text a password reset link" />
    </div>
  );
  if (!invite || !invite.token) return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <button onClick={onRegenerate} className="text-[11.5px] text-[#0055FF] font-semibold" data-testid={`${testId}-regen`}>Generate</button>
      <SendBtn label="Email + text a set-up link now" />
    </div>
  );
  const url = `${(process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "")}/hub-invite/${invite.token}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Invite link copied"); } catch { toast.error("Copy failed"); } };
  return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <button onClick={copy} className="inline-flex items-center gap-1 px-2 h-7 rounded border border-slate-300 hover:border-slate-400 bg-white text-[11px] font-semibold text-slate-700" data-testid={`${testId}-copy`}>
        {copied ? <><Check className="size-3 text-teal-500" /> Copied</> : <><Link2 className="size-3" /> Invite</>}
      </button>
      <SendBtn label="Email + text the set-up link now" />
      <button onClick={onRegenerate} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Regenerate" data-testid={`${testId}-regen`}><RefreshCw className="size-3.5" /></button>
      {invite.expired && <span className="text-[10.5px] text-[#0044CC] ml-1 font-semibold">expired</span>}
    </div>
  );
}

function F({ label, children }) { return <div><Label className="text-[10.5px] uppercase tracking-[0.14em] text-slate-600 font-semibold">{label}</Label><div className="mt-1.5">{children}</div></div>; }
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function StatementDialog({ party, onOpenChange }) {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [busy, setBusy] = useState(false);
  if (!party) return null;

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/hub/referrers/${party.id}/statement/${year}/${month}.pdf`, { params: { v: Date.now() }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `connexis-commission-${(party.name || "introducer").toLowerCase().replace(/\s+/g, "-")}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Statement for ${MONTH_NAMES[month - 1]} ${year} downloaded`);
    } catch (e) {
      let detail = "";
      try { detail = JSON.parse(await e?.response?.data?.text())?.detail || ""; } catch { /* not JSON */ }
      toast.error(detail || "Statement download failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!party} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="hub-statement-dialog">
        <DialogHeader>
          <DialogTitle>Commission statement</DialogTitle>
          <DialogDescription>{party.name}{party.company ? ` · ${party.company}` : ""} — pick a period and pull the branded PDF.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="flex-1" data-testid="hub-statement-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-28" data-testid="hub-statement-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getUTCFullYear(), now.getUTCFullYear() - 1, now.getUTCFullYear() - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={download} disabled={busy} className="w-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="hub-statement-download">
          {busy ? <Loader2 className="size-4 animate-spin" /> : <><FileDown className="size-3.5 mr-1.5" /> Download statement</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
